/**
 * Redaction and sanitization helpers to prevent sensitive data leakage.
 */
import { clamp } from "./utils";

export type RedactOptions = {
  redactKeys: string[];
  allowedQueryParams: string[];
  maxDepth?: number;
  maxStringLength?: number;
  maxArrayLength?: number;
};

const defaultOptions: Required<
  Pick<RedactOptions, "maxDepth" | "maxStringLength" | "maxArrayLength">
> = {
  maxDepth: 4,
  maxStringLength: 200,
  maxArrayLength: 50,
};

export type Redactor = {
  sanitizeValue: (value: unknown, depth?: number) => unknown;
  sanitizeObject: (value: Record<string, unknown>) => Record<string, unknown>;
  sanitizeUrl: (url: string | undefined) => string | undefined;
  hashString: (value: string) => string;
};

export function createRedactor(options: RedactOptions): Redactor {
  const cfg = { ...defaultOptions, ...options };
  const lowerKeys = cfg.redactKeys.map((key) => key.toLowerCase());
  const allowedParams = new Set(
    cfg.allowedQueryParams.map((key) => key.toLowerCase()),
  );

  const nonRedactableKeys = new Set([
    "email_hash",
    "phone_hash",
    "external_id_hash",
  ]);
  const urlKeys = new Set([
    "url",
    "href",
    "referrer",
    "referer",
    "canonical",
    "filename",
  ]);

  const hashString = (value: string) => {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i += 1) {
      hash ^= value.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16);
  };

  const shouldRedactKey = (key: string) => {
    const lower = key.toLowerCase();
    if (nonRedactableKeys.has(lower)) return false;
    return lowerKeys.some((needle) => lower.includes(needle));
  };

  const sanitizeUrl = (url: string | undefined) => {
    if (!url) return url;
    try {
      const base =
        typeof window === "undefined"
          ? "http://localhost"
          : window.location.href;
      const parsed = new URL(url, base);
      const params = new URLSearchParams();
      parsed.searchParams.forEach((value, key) => {
        if (allowedParams.has(key.toLowerCase())) params.append(key, value);
      });
      parsed.search = params.toString();
      parsed.hash = "";
      return parsed.toString();
    } catch {
      return url.split("?")[0];
    }
  };

  const sanitizeValue = (value: unknown, depth = 0): unknown => {
    if (value === null || value === undefined) return value;
    if (depth > cfg.maxDepth) return undefined;
    if (typeof value === "string") {
      return value.length > cfg.maxStringLength
        ? `${value.slice(0, cfg.maxStringLength)}...`
        : value;
    }
    if (typeof value === "number" || typeof value === "boolean") return value;
    if (typeof value === "function") return undefined;
    if (Array.isArray(value)) {
      return value
        .slice(0, cfg.maxArrayLength)
        .map((item) => sanitizeValue(item, depth + 1));
    }
    if (typeof value === "object") {
      const hasNode = typeof Node !== "undefined";
      const hasWindow = typeof Window !== "undefined";
      if (
        (hasNode && value instanceof Node) ||
        (hasWindow && value instanceof Window)
      ) {
        return undefined;
      }
      const output: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(
        value as Record<string, unknown>,
      )) {
        if (shouldRedactKey(key)) continue;
        const keyLower = key.toLowerCase();
        const nextValue =
          typeof val === "string" && urlKeys.has(keyLower)
            ? sanitizeUrl(val)
            : val;
        const sanitized = sanitizeValue(nextValue, depth + 1);
        if (sanitized !== undefined) {
          output[key] = sanitized;
        }
      }
      return output;
    }
    return undefined;
  };

  const sanitizeObject = (value: Record<string, unknown>) => {
    const sanitized = sanitizeValue(value, 0);
    return typeof sanitized === "object" && sanitized !== null
      ? (sanitized as Record<string, unknown>)
      : {};
  };

  return { sanitizeValue, sanitizeObject, sanitizeUrl, hashString };
}

export function clampPercent(value: number): number {
  return clamp(value, 0, 100);
}
